import React, {useState, useEffect} from "react";
import {
    Box, Typography, Paper, Grid, CircularProgress, Chip, Button,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Collapse,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ArticleIcon from "@mui/icons-material/Article";
import SettingsIcon from "@mui/icons-material/Settings";
import ListAltIcon from "@mui/icons-material/ListAlt";
import BarChartIcon from "@mui/icons-material/BarChart";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {useNavigate} from "react-router-dom";
import {accountingApi} from "../../services/api/accounting";
import {getLandlordId} from "../../services/api/client";

const today = new Date();
const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split("T")[0];
const lastOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split("T")[0];

const formatMoney = (amount) => {
    if (amount === null || amount === undefined) return "KES 0.00";
    const num = Number(amount);
    const formatted = Math.abs(num).toLocaleString("en-KE", {minimumFractionDigits: 2, maximumFractionDigits: 2});
    return num < 0 ? `(KES ${formatted})` : `KES ${formatted}`;
};

const REF_TYPE_COLORS = {
    RENT_PAYMENT: "#2e7d32", EXPENSE: "#e65100", PENALTY: "#c62828",
    ARREAR: "#f57f17", DEPOSIT: "#1565c0", DEPOSIT_REFUND: "#00838f",
    DEPOSIT_FORFEITED: "#6a1b9a", DEPOSIT_INCOME: "#00695c",
    LANDLORD_PAYOUT: "#283593", MANUAL: "#616161", MIGRATION: "#795548",
};

function SummaryCard({title, value, icon, color, onClick}) {
    return (
        <Paper
            sx={{
                p: 2.5, borderRadius: 2, cursor: onClick ? "pointer" : "default",
                borderLeft: `4px solid ${color}`,
                "&:hover": onClick ? {bgcolor: "#f5f7fa", transform: "translateY(-1px)", transition: "all 0.2s"} : {},
            }}
            onClick={onClick}
        >
            <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between"}}>
                <Box>
                    <Typography variant="body2" color="textSecondary" sx={{mb: 0.5}}>{title}</Typography>
                    <Typography variant="h5" fontWeight={700} sx={{color}}>{value}</Typography>
                </Box>
                <Box sx={{bgcolor: `${color}15`, borderRadius: 2, p: 1.5}}>
                    {icon}
                </Box>
            </Box>
        </Paper>
    );
}

function RecentEntryRow({entry}) {
    const [open, setOpen] = useState(false);
    return (
        <>
            <TableRow sx={{cursor: "pointer", "&:hover": {bgcolor: "#f5f5f5"}}} onClick={() => setOpen(!open)}>
                <TableCell sx={{width: 30, py: 1}}>
                    <IconButton size="small">{open ? <KeyboardArrowUpIcon fontSize="small"/> : <KeyboardArrowDownIcon fontSize="small"/>}</IconButton>
                </TableCell>
                <TableCell sx={{py: 1}}>{new Date(entry.entry_date).toLocaleDateString("en-KE", {month: "short", day: "numeric"})}</TableCell>
                <TableCell sx={{py: 1, maxWidth: 250}}><Typography variant="body2" noWrap>{entry.description}</Typography></TableCell>
                <TableCell sx={{py: 1}}>
                    <Chip label={entry.reference_type.replace(/_/g, " ")} size="small"
                        sx={{bgcolor: REF_TYPE_COLORS[entry.reference_type] || "#616161", color: "#fff", fontWeight: 600, fontSize: "9px"}}/>
                </TableCell>
                <TableCell align="right" sx={{py: 1, fontWeight: 500}}>
                    {formatMoney(entry.lines?.reduce((sum, l) => sum + (l.debit || 0), 0))}
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell colSpan={5} sx={{py: 0, borderBottom: open ? undefined : "none"}}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{py: 1, px: 2}}>
                            <Table size="small">
                                <TableBody>
                                    {entry.lines?.map((line) => (
                                        <TableRow key={line.id}>
                                            <TableCell sx={{fontFamily: "monospace", fontSize: "11px", border: "none"}}>{line.code}</TableCell>
                                            <TableCell sx={{fontSize: "11px", border: "none"}}>{line.name}</TableCell>
                                            <TableCell align="right" sx={{color: "#1565c0", fontSize: "11px", border: "none"}}>{line.debit > 0 ? formatMoney(line.debit) : "-"}</TableCell>
                                            <TableCell align="right" sx={{color: "#2e7d32", fontSize: "11px", border: "none"}}>{line.credit > 0 ? formatMoney(line.credit) : "-"}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

export default function AccountingDashboard() {
    const [loading, setLoading] = useState(true);
    const [plData, setPlData] = useState(null);
    const [recentEntries, setRecentEntries] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        const landlordId = getLandlordId();
        try {
            const [plResponse, journalResponse] = await Promise.all([
                accountingApi.getProfitAndLoss({landlord_id: landlordId, start_date: firstOfMonth, end_date: lastOfMonth}),
                accountingApi.getJournalEntries({landlord_id: landlordId, page: 1, page_size: 10}),
            ]);

            if (plResponse.ok && plResponse.data) setPlData(plResponse.data);
            if (journalResponse.ok && journalResponse.data?.entries) setRecentEntries(journalResponse.data.entries);
        } catch (err) {
            console.error("Failed to fetch dashboard data:", err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <Box sx={{display: "flex", justifyContent: "center", py: 10}}><CircularProgress/></Box>;
    }

    const currentMonthName = today.toLocaleDateString("en-KE", {month: "long", year: "numeric"});

    return (
        <Box sx={{py: 3}}>
            <Typography variant="h5" fontWeight={600} sx={{mb: 0.5, color: "#2C5283"}}>
                Accounting Overview
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{mb: 3}}>
                {currentMonthName} Summary
            </Typography>

            {/* Summary Cards */}
            <Grid container spacing={2.5} sx={{mb: 4}}>
                <Grid item xs={12} sm={6} md={3}>
                    <SummaryCard
                        title="Total Revenue"
                        value={formatMoney(plData?.total_revenue || 0)}
                        icon={<TrendingUpIcon sx={{color: "#2e7d32", fontSize: 28}}/>}
                        color="#2e7d32"
                        onClick={() => navigate("/accounting/reports/profit-and-loss")}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <SummaryCard
                        title="Total Expenses"
                        value={formatMoney(plData?.total_expenses || 0)}
                        icon={<TrendingDownIcon sx={{color: "#e65100", fontSize: 28}}/>}
                        color="#e65100"
                        onClick={() => navigate("/accounting/reports/profit-and-loss")}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <SummaryCard
                        title="Net Income"
                        value={formatMoney(plData?.net_income || 0)}
                        icon={<AccountBalanceIcon sx={{color: (plData?.net_income || 0) >= 0 ? "#1565c0" : "#c62828", fontSize: 28}}/>}
                        color={(plData?.net_income || 0) >= 0 ? "#1565c0" : "#c62828"}
                        onClick={() => navigate("/accounting/reports/profit-and-loss")}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <SummaryCard
                        title="Estimated Tax"
                        value={formatMoney(plData?.estimated_tax || 0)}
                        icon={<ReceiptLongIcon sx={{color: "#c62828", fontSize: 28}}/>}
                        color="#c62828"
                        onClick={() => navigate("/accounting/reports/income-statement")}
                    />
                </Grid>
            </Grid>

            {/* Quick Links */}
            <Grid container spacing={2} sx={{mb: 4}}>
                {[
                    {label: "Chart of Accounts", path: "/accounting/accounts", icon: <ListAltIcon/>},
                    {label: "Journal Entries", path: "/accounting/journal", icon: <ArticleIcon/>},
                    {label: "Reports", path: "/accounting/reports/profit-and-loss", icon: <BarChartIcon/>},
                    {label: "Settings", path: "/accounting/settings", icon: <SettingsIcon/>},
                ].map((item) => (
                    <Grid item xs={6} sm={3} key={item.path}>
                        <Paper
                            sx={{
                                p: 2, borderRadius: 2, textAlign: "center", cursor: "pointer",
                                "&:hover": {bgcolor: "#f0f7ff", transform: "translateY(-1px)", transition: "all 0.2s"},
                            }}
                            onClick={() => navigate(item.path)}
                        >
                            <Box sx={{color: "#2C5283", mb: 0.5}}>{item.icon}</Box>
                            <Typography variant="body2" fontWeight={500}>{item.label}</Typography>
                        </Paper>
                    </Grid>
                ))}
            </Grid>

            {/* Recent Journal Entries */}
            <Paper sx={{borderRadius: 2, overflow: "hidden"}}>
                <Box sx={{px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #e0e0e0"}}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{color: "#2C5283"}}>Recent Journal Entries</Typography>
                    <Button size="small" onClick={() => navigate("/accounting/journal")} sx={{textTransform: "none"}}>View All</Button>
                </Box>
                {recentEntries.length === 0 ? (
                    <Box sx={{py: 4, textAlign: "center"}}>
                        <Typography color="textSecondary">No journal entries yet. They are created automatically when financial events occur.</Typography>
                    </Box>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{bgcolor: "#f5f7fa"}}>
                                    <TableCell sx={{width: 30}}/>
                                    <TableCell sx={{fontWeight: 600, width: 90}}>Date</TableCell>
                                    <TableCell sx={{fontWeight: 600}}>Description</TableCell>
                                    <TableCell sx={{fontWeight: 600, width: 140}}>Type</TableCell>
                                    <TableCell align="right" sx={{fontWeight: 600, width: 130}}>Amount</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {recentEntries.map(entry => <RecentEntryRow key={entry.id} entry={entry}/>)}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        </Box>
    );
}
